import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/inspire.css';
import '../styles/inspire-home.css';
import '../styles/service-page.css';

const TherapyForChildren = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const helpItems = [
    "Anxiety, worry, or fear that feels bigger than the situation",
    "Big emotions — meltdowns, anger, frustration, or shutting down",
    "ADHD-related challenges at home or school",
    "Difficulty with transitions, social skills, or self-regulation",
    "School avoidance, refusal, or academic stress",
    "Behavioral challenges that feel hard to manage",
    "Adjusting to divorce, moves, grief, or family changes",
    "Trouble making or keeping friends"
  ];

  const fitItems = [
    "Your child seems to be struggling emotionally but can't always tell you why",
    "You've noticed changes in behavior at home or school",
    "Teachers, pediatricians, or school staff have raised concerns",
    "Your child has an IEP or 504 plan — or you're wondering if they need one",
    "You want a therapist who gets kids, schools, and development — not just a diagnosis",
    "You want your child to feel safe, seen, and supported"
  ];

  return (
    <div className="therapy-site" data-testid="therapy-for-children-page">
      <Helmet>
        <title>Therapy for Children | Inspire Therapy Collective - Lake Nona, Orlando</title>
        <meta name="description" content="Playful, developmentally attuned therapy for children navigating big emotions, anxiety, ADHD, and behavioral challenges in Lake Nona, Orlando and throughout Florida and Virginia." />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="service-hero">
        <div 
          className="service-hero-overlay" 
          style={{ backgroundImage: "url('https://images.pexels.com/photos/12788663/pexels-photo-12788663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
        />
        <div className="service-hero-gradient" />
        <div className="service-hero-content fade-in-up">
          <h1 className="gradient-text" data-testid="children-page-title">Therapy for Children</h1>
          <p className="service-tagline">
            Playful, developmentally attuned therapy for children navigating big emotions, anxiety, ADHD, behavioral challenges, or school stress.
          </p>
          <span className="service-location-tag">Lake Nona, Orlando FL &bull; Telehealth across Florida and Virginia</span>
        </div>
      </section>

      {/* Intro */}
      <section className="service-intro">
        <div className="container">
          <div className="service-intro-content fade-in-up">
            <p>
              Children don't always have the words for what they're feeling — but that doesn't mean they aren't feeling it deeply. Anxiety, frustration, school stress, social struggles, and big emotions often show up through behavior long before a child can explain what's going on inside.
            </p>
            <p>
              With over two decades of experience in school settings, as an ABA therapist, and as a licensed clinical practitioner, I understand the whole child — across home, school, and social settings. I'm well-versed in IEPs, 504 plans, and how to advocate within school systems so your child gets the support they need in every environment.
            </p>
            <p>
              Therapy with me is warm, playful, and grounded in what actually works. I meet children where they are — using play, creativity, and connection to help them process emotions, build coping skills, and develop confidence. My goal isn't just to manage behavior — it's to help your child understand themselves and feel truly supported.
            </p>
          </div>
        </div>
      </section>

      {/* What I Help With */}
      <section className="service-help-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text" data-testid="children-help-heading">What I Help With</h2>
          <div className="service-help-grid">
            {helpItems.map((item, index) => (
              <div key={index} className="service-help-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="service-help-icon">
                  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id={`helpIcon${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F9C8A8" />
                        <stop offset="50%" stopColor="#E6A9C0" />
                        <stop offset="100%" stopColor="#C4A8E2" />
                      </linearGradient>
                    </defs>
                    <circle cx="14" cy="14" r="10" stroke={`url(#helpIcon${index})`} strokeWidth="1.5" fill="none" opacity="0.3" />
                    <path d="M10 14L13 17L18 11" stroke={`url(#helpIcon${index})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p>{item}</p>
              </div>
            ))}
          </div>

          <div className="violet-badge-inline fade-in-up">
            <a href="https://www.joinviolet.com" target="_blank" rel="noopener noreferrer" className="violet-badge-link" data-testid="violet-badge-children">
              <img 
                src="https://app.joinviolet.com/api/v1/users/ZjcxNzc3Y2QtMWJhMy00ZDE3LWIzNjQtN2I3N2U5ODRiNjRk/benchmark_image?communities=neurodivergence" 
                alt="Violet Verified — Neurodivergence Cultural Awareness" 
                className="violet-badge-img"
              />
            </a>
            <p className="body-small violet-badge-text">My work with neurodivergent children is backed by Violet-verified training in neurodivergent care.</p>
          </div>
        </div>
      </section>

      {/* My Approach */}
      <section className="service-approach">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">My Approach with Children</h2>
          <svg className="service-sun-divider fade-in" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="sunDividerChildren" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F9C8A8" />
                <stop offset="50%" stopColor="#E6A9C0" />
                <stop offset="100%" stopColor="#C4A8E2" />
              </linearGradient>
            </defs>
            <line x1="10" y1="45" x2="70" y2="45" stroke="url(#sunDividerChildren)" strokeWidth="1.5" opacity="0.35" />
            <path d="M 40 45 A 15 15 0 0 1 25 45 L 40 45 Z" fill="url(#sunDividerChildren)" opacity="0.75" />
          </svg>
          <div className="service-approach-content fade-in-up">
            <p>
              I use play-based, evidence-informed modalities including Cognitive Behavioral Therapy (CBT), mindfulness, and creative expression — all adapted to be developmentally appropriate and genuinely engaging for kids.
            </p>
            <p>
              My background as a school social worker means I understand behavior, development, classroom dynamics, and how to collaborate with educators and school teams. I don't just work with your child in a therapy room — I help bridge the gap between therapy, home, and school so support is consistent across all environments.
            </p>
            <p>
              I also believe in supporting parents throughout the process. You know your child better than anyone, and I'll work alongside you to make sure the strategies we build in session carry over into everyday life.
            </p>
          </div>
        </div>
      </section>

      {/* Good Fit */}
      <section className="service-fit-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">This Might Be a Good Fit If...</h2>
          <div className="service-fit-list">
            {fitItems.map((item, index) => (
              <div key={index} className="service-fit-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="fit-bullet" />
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step Up Scholarship Callout */}
      <section className="service-intro" data-testid="step-up-callout-children">
        <div className="container">
          <div className="step-up-callout fade-in-up">
            <p>Enrolled in a Step Up For Students scholarship? We're on the EMA Marketplace and bill your child's scholarship directly for counseling.</p>
            <a href="/step-up-scholarship" className="step-up-callout-link" data-testid="step-up-link-children">Learn more &rarr;</a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-cta">
        <div className="container">
          <div className="service-cta-content fade-in-up">
            <h2 className="heading-2 font-serif gradient-text">Ready to Support Your Child?</h2>
            <p>
              You don't need to have it all figured out. If something feels off, that's enough of a reason to reach out. Let's talk about what your child needs and how I can help.
            </p>
            <div className="hero-cta-group" style={{ justifyContent: 'center' }}>
              <button onClick={goToContact} className="btn btn-primary" data-testid="children-cta-primary">
                Book a Free 15-Min Consult
              </button>
              <button onClick={() => navigate('/', { state: { scrollTo: 'insurance' } })} className="btn btn-secondary-outline" data-testid="children-cta-secondary">
                Check Insurance & Book
              </button>
            </div>
            <div className="service-cta-details">
              <a href="tel:4072019895" className="cta-phone">(407) 201-9895</a>
              <span className="cta-location">Lake Nona, Orlando FL &bull; Telehealth across Florida and Virginia</span>
            </div>
            <button onClick={() => navigate('/', { state: { scrollTo: 'services' } })} className="explore-all-link" data-testid="explore-all-services-link">
              &larr; Explore All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TherapyForChildren;
